import CDS from "design-system";
import { roleStyles } from "components/Tag/UserTag/helpers";
import type { UserRole } from "features/User/models";

const ROLE_LABEL: Record<string, string> = {
  new_hire: "New hire",
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "People Team",
  recruiter: "Recruiter",
  it_team_contact: "IT",
};

/**
 * A "recipient-like" object — the minimum shape every recipient-picking
 * select returns. Each select can adapt its own option type to this.
 */
export interface RecipientLike {
  /** Backend record kind. Anything outside the known set falls through to
   *  the caller's default chip. */
  recordType:
  | "User"
  | "OnboardingJourneyNetworkRelation"
  | "CustomEmail"
  | "MessageServiceChannel"
  | string
  | undefined;
  label: string;
  /** Required only for roles — used to resolve the role's color palette
   *  and humanized label. */
  name?: string;
  /** User-variant avatar URL. */
  avatarUrl?: string;
  /** Custom-email-variant address (shown inside the chip). */
  email?: string;
}

interface RecipientChipFromOptionProps {
  recipient: RecipientLike;
  size?: "xs" | "sm" | "md";
  bordered?: boolean;
  onRemove?: () => void;
}

/**
 * Shared adapter that maps any recipient-like option to the design-system
 * RecipientChip. Used by every select that picks people/roles/emails so
 * each one renders the same chip styling without re-implementing the
 * recordType branching. Returns `null` for unknown kinds — the caller can
 * fall back to a default (e.g. chakra-react-select's MultiValue) for those.
 */
export default function RecipientChipFromOption({
  recipient,
  size = "sm",
  bordered = true,
  onRemove,
}: RecipientChipFromOptionProps) {
  if (recipient.recordType === "User") {
    return (
      <CDS.RecipientChip
        variant="user"
        label={recipient.label}
        avatarUrl={recipient.avatarUrl}
        size={size}
        bordered={bordered}
        remove={onRemove}
      />
    );
  }
  if (recipient.recordType === "OnboardingJourneyNetworkRelation") {
    const roleName = recipient.name ?? "";
    const styles = roleStyles(roleName as UserRole);
    return (
      <CDS.RecipientChip
        variant="role"
        label={ROLE_LABEL[roleName] ?? recipient.label}
        roleColors={{ bg: styles.light, fg: styles.dark }}
        size={size}
        bordered={bordered}
        remove={onRemove}
      />
    );
  }
  if (recipient.recordType === "CustomEmail") {
    return (
      <CDS.RecipientChip
        variant="email"
        label={recipient.email || recipient.label}
        size={size}
        bordered={bordered}
        remove={onRemove}
      />
    );
  }
  return null;
}
